/**
 * 会社の切替・会社を選択（#151、docs/60 §2・§6、Figma ①〜⑥。040＝docs/29・#99＝docs/38 の作りを引き継ぐ）
 *   2つの形：ログイン中の切替（/choose?switch、useMe() の companies、題「会社の切替」）と、
 *            ログインの続き（/choose、location.state の ChooseResponse＋email、題「会社を選択」）
 *   ・iOS の Inset Grouped：机の地に白いまとまり（components/GroupList.tsx）。製品ごとの節（API の並びのまま＝いまのドメインの製品が先）。
 *     製品が1つだけの人には節の見出しを出さない（★f）。ほかの製品の節の下に「{製品名}のサイトへ切替」
 *   ・行：会社のアイコン（ロゴか頭文字、components/CompanyIcon.tsx）＋会社名（1行・末尾「…」）。使用中＝太字＋「使用中」＋✓（押せない）／
 *     ほかの製品＝↗（押すと相手のドメインへ移ってそのまま入る＝handoff、★1）／前回（ログインの続きだけ）／担当者／使えない＝薄く・理由（朱）・押せない
 *   ・「編集」（切替のときだけ。★g）：上の帯が「取消｜並び替え｜決定」、一番上に「並び順」（よく使う順／自分で並べる）、行の右につまみ。
 *     つまみを押さえて上下に動かす か 押して「一番上へ／上へ／下へ／一番下へ」（ドラッグだけにしない、docs/10 §5.5）。
 *     「決定」で PUT /account/company-order（返る Me を入れる＝再読込しても残る）、「取消」で元に戻す
 *   ・「新しい会社の作成」の行（#185 で「会社を追加」から改名・#186 で「新しい会社を作る」から。PC だけ＝スマホはメニューから消し、一番下に「会社の追加・料金プラン・退会はパソコンから操作します。」）
 *     → 窓（#185、docs/74 §5。Figma「招待と会社の追加（10/14）」④ → #287、Figma「文言と画面の見直し（11/1）」⑥）：題「新しい会社の作成」・
 *     1文「別の会社（事業所）を新しく作成します。作成者が管理者になります。」・
 *     注意の帯（#287：朱はエラーに見える＝藍の淡い地・藍の字・左に ⓘ）「○○ に利用者を追加する操作ではありません。利用者の追加は 設定 → ユーザー管理 →「ユーザー追加」から行います。」・
 *     会社名（屋号）必須・業種 →「取消」「作成」。/choose?switch&new（アカウントのメニュー）は開いた形で始める（スマホでは開かない）。
 *     氏名がそろっていない人だけ「あなたの氏名」（姓・名。#152、docs/61 §2 ⑦）→ 人と全部の会社に入る
 *   ・一番下に「ログアウト」（朱）と「{メール} でログイン中」（帯の右のメールアドレスはやめた）
 *   切替：ログインしていなければログインへ。ログインの続き：state が無ければ（開き直した）ログインへ。切替は題の左上に戻るボタン（#98）
 */
import { HOME_MSG,
  ACCOUNT_NAME_MSG,
  DEFAULT_INDUSTRY,
  NEW_COMPANY_MSG,
  NewCompanyName,
  PC_ONLY_NOTE,
  PRODUCTS,
  SWITCHER_MSG,
  USER_MSG,
  type ChooseHandoffRequest,
  type ChooseResponse,
  type CompanyChoice,
  type CompanyOrder,
  type CompanyOrderRequest,
  type HandoffStartRequest,
  type HandoffStartResponse,
  type Industry,
  type Me,
  type Product,
} from "@genbaryoku/contract";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { Navigate, useLocation, useNavigate, useSearchParams } from "react-router";
import { ApiError, api, messageOf } from "../api.js";
import { useLogout, useMe, setMe } from "../auth.js";
import { BackButton, useBackNav, usePageName } from "../components/BackButton.jsx";
import { CompanyIcon } from "../components/CompanyIcon.jsx";
import { CheckMark, GripButton, GroupInputRow, GroupList, GroupRow, GroupSection, MoveMenu, useFlip, useRowDrag, type MoveKind } from "../components/GroupList.jsx";
import { IndustrySelect } from "../components/IndustrySelect.jsx";
import { InfoIcon } from "../components/icons.jsx";
import { AuthLogo, ErrorText, Field, SiteFooter, guardComposingEnter } from "../components/ui.jsx";
import { usePcOnly, usePcOnlyHere } from "../lib/pcOnly.js";
import { LoginExpiredStop, PcOnlyStop } from "../components/StopScreen.jsx";
import { NoticeBand, useNotices } from "../components/NoticeBand.jsx";
import { useProduct } from "../lib/useProduct.js";
import { afterLoginPath } from "../lib/autoLogout.js";
import { useLogoHeader } from "../components/LightArea.jsx";

/**
 * 会社を選ぶ鍵（ログインの続き、10分・1回きり）が使えない：時間が経った・使った・同じ入口でもう一度ログインした（#203）。
 * どの会社を押しても通らないので、止めの画面 j)「ログインの有効期限が切れました」と「ログイン画面へ」を出す（2026-10-17 夜 射場さん。
 * 前は題の下の朱の1行）。ほかの製品のタブでのログインでは切れない＝DB が入口の製品ごとに持つ
 */
const isChooseExpired = (e: unknown) => e instanceof ApiError && e.code === "AUTH_CHOOSE_EXPIRED";

const MOVE_LABELS: Record<MoveKind, string> = { top: SWITCHER_MSG.moveTop, up: SWITCHER_MSG.moveUp, down: SWITCHER_MSG.moveDown, bottom: SWITCHER_MSG.moveBottom };

/** 切替のときは /me の companies を、ログインの続きの会社の一覧と同じ形に */
function fromMe(me: Me): CompanyChoice[] {
  return me.companies.map((c) => ({
    tenantId: c.tenantId,
    name: c.name,
    role: c.role,
    own: c.own,
    joinedAt: null,
    usable: c.usable,
    blockedReason: c.blockedReason,
    last: false,
    current: c.current,
    product: c.product,
    selectable: c.selectable,
    reason: c.reason,
    logoUrl: c.logoUrl ?? null,
    initial: c.initial,
    autoRank: c.autoRank ?? 0,
  }));
}

/** 製品の節。movable＝並べ替えられる会社（使える会社）、blocked＝使えない会社（いつも節の一番下・並べ替えない。★c） */
type Section = { product: Product; items: CompanyChoice[]; movable: CompanyChoice[]; blocked: CompanyChoice[] };

/** 並びのまま製品で分ける（最初に出てくる製品が先。いまのドメインの製品はいつも先） */
function sectionsOf(list: CompanyChoice[], domain: Product): Section[] {
  const out: Section[] = [];
  for (const c of list) {
    let s = out.find((x) => x.product === c.product);
    if (!s) out.push((s = { product: c.product, items: [], movable: [], blocked: [] }));
    s.items.push(c);
    (c.usable ? s.movable : s.blocked).push(c);
  }
  return out.sort((a, b) => Number(a.product !== domain) - Number(b.product !== domain));
}

/** 並べ替えの下書き（「編集」のあいだだけ）。ids＝全部の会社を表示の順に（節を上から順につなげる） */
type Draft = { mode: CompanyOrder; ids: string[] };

function ExtIcon() {
  return (
    <svg className="gmark gmark-ext" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M14 5h5v5M19 5l-8 8M17 14v4a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V8a1 1 0 0 1 1-1h4" />
    </svg>
  );
}

/**
 * /choose?new（新しい会社の作成）を スマホで開いた（#186、docs/75 a）：画面の中身を描かず「パソコンから操作してください」。
 * ?new の無い会社の切替・会社を選択はスマホでもそのまま
 */
export function ChoosePage() {
  return usePcOnlyHere() ? <PcOnlyStop /> : <ChooseBody />;
}

function ChooseBody() {
  const loc = useLocation();
  const nav = useNavigate();
  const qc = useQueryClient();
  const [sp] = useSearchParams();
  const me = useMe();
  const logout = useLogout();
  const P = useProduct();
  const logoHeader = useLogoHeader(P.product); // ダークでは暗い地用（#191 の2回目）
  const switching = sp.has("switch");
  // ログイン前の帯（docs/95）：ログインの続き（会社の選択）だけ。会社の切替（ログイン後）はホームの帯が受け持つ
  const notices = useNotices(!switching);
  const st = loc.state as (ChooseResponse & { email?: string }) | null;
  const [err, setErr] = useState<string | undefined>();
  /** 会社を選ぶ鍵が使えない（#203）：断りの文と「ログイン画面へ」 */
  const [expired, setExpired] = useState(false);
  /** 押した会社（待つあいだ回る印・ほかの行は押せない） */
  const [busyId, setBusyId] = useState<string | null>(null);
  /** ほかの製品へ移る途中（読み上げ「移動しています」） */
  const [moving, setMoving] = useState(false);

  // 新しい会社を作る（#99 → #151 でシート → #185 で窓・PC だけ）：?new（アカウントのメニューの「新しい会社を作る」）なら開いた形で。スマホでは出さない
  const pcOnly = usePcOnly();
  const canCreate = !pcOnly.hidden("/choose?new");
  const [creating, setCreating] = useState(sp.has("new") && canCreate);
  const [coName, setCoName] = useState("");
  // 業種（#142 段4）：はじめから製品の既定。分類と品目の見本が業種で入る
  const [industry, setIndustry] = useState<Industry>(DEFAULT_INDUSTRY[P.product]);
  const [createErr, setCreateErr] = useState<string | undefined>();
  const parsed = NewCompanyName.safeParse(coName);
  // あなたの氏名（#152）：氏名がそろっていない人だけ聞く（ログイン中は Me、ログインの続きは ChooseResponse の account）。届いている分は入れておく
  const acct = switching ? me.data?.account : st?.account;
  const askName = !!acct?.needsName;
  const [nf, setNf] = useState("");
  const [ng, setNg] = useState("");
  useEffect(() => {
    if (!acct) return;
    setNf(acct.familyName);
    setNg(acct.givenName);
  }, [acct?.familyName, acct?.givenName]); // eslint-disable-line react-hooks/exhaustive-deps
  const nameOk = !askName || (nf.trim() !== "" && ng.trim() !== "");
  const tooLong = coName.trim().length > 100;
  const back = useBackNav({ to: "/home", label: HOME_MSG.menu }, creating && coName.trim() !== "");

  // 並び替え（「編集」）
  const [draft, setDraft] = useState<Draft | null>(null);
  const [saving, setSaving] = useState(false);
  /** メニューを開いているつまみの会社 */
  const [menuId, setMenuId] = useState<string | null>(null);
  const gripEls = useRef(new Map<string, HTMLButtonElement>());
  /** 動かしたあと、その行のつまみへ focus を戻す（描いたあと） */
  const focusGrip = useRef<string | null>(null);

  const title = switching ? SWITCHER_MSG.title : SWITCHER_MSG.chooseTitle;
  usePageName(title);

  const companies: CompanyChoice[] = switching ? (me.data ? fromMe(me.data) : []) : (st?.companies ?? []);
  const byId = new Map(companies.map((c) => [c.tenantId, c]));
  // 編集のあいだは下書きの並び（あとで増えた会社は節の下）
  const list = draft ? [...draft.ids.flatMap((id) => byId.get(id) ?? []), ...companies.filter((c) => !draft.ids.includes(c.tenantId))] : companies;
  const sections = sectionsOf(list, P.product);
  const sectionOf = (id: string) => sections.find((s) => s.items.some((c) => c.tenantId === id));

  const { drag, rows, rowRef, gripProps } = useRowDrag({
    onDrop: (id, to) => {
      const s = sectionOf(id);
      if (!s) return;
      snapshot(); // 離した所から滑って収まる（動かさなかったときも元へ戻る）
      const ids = s.movable.map((c) => c.tenantId);
      if (ids.indexOf(id) !== to) placeAt(s, id, to);
    },
    onTap: (id) => setMenuId((m) => (m === id ? null : id)),
  });
  const snapshot = useFlip(rows);

  useEffect(() => {
    if (drag) setMenuId(null);
  }, [drag?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const id = focusGrip.current;
    if (!id) return;
    focusGrip.current = null;
    gripEls.current.get(id)?.focus();
  });

  /** 節の使える会社の並びを movIds にする（使えない会社は下のまま）。動かしたら「自分で並べる」 */
  const setSection = (s: Section, movIds: string[], mode: CompanyOrder) => {
    const ids = sections.flatMap((x) => (x.product === s.product ? [...movIds, ...x.blocked.map((c) => c.tenantId)] : [...x.movable, ...x.blocked].map((c) => c.tenantId)));
    setDraft({ mode, ids });
  };
  const placeAt = (s: Section, id: string, to: number) => {
    const ids = s.movable.map((c) => c.tenantId).filter((x) => x !== id);
    ids.splice(Math.max(0, Math.min(ids.length, to)), 0, id);
    setSection(s, ids, "manual");
  };
  const moveBy = (s: Section, id: string, kind: MoveKind) => {
    const ids = s.movable.map((c) => c.tenantId);
    const k = ids.indexOf(id);
    const to = kind === "top" ? 0 : kind === "up" ? k - 1 : kind === "down" ? k + 1 : ids.length - 1;
    setMenuId(null);
    focusGrip.current = id;
    if (k < 0 || to < 0 || to >= ids.length || to === k) return;
    snapshot();
    placeAt(s, id, to);
  };
  const startEdit = () => {
    setErr(undefined);
    setMenuId(null);
    setDraft({ mode: me.data?.companyOrder ?? "auto", ids: sections.flatMap((s) => [...s.movable, ...s.blocked].map((c) => c.tenantId)) });
  };
  const cancelEdit = () => {
    setErr(undefined);
    setMenuId(null);
    setDraft(null);
  };
  /** よく使う順：その場で各節を autoRank の順に（200ms で動く） */
  const toAuto = () => {
    if (!draft) return;
    snapshot();
    const ids = sections.flatMap((s) => [...[...s.movable].sort((a, b) => (a.autoRank ?? 0) - (b.autoRank ?? 0)), ...s.blocked].map((c) => c.tenantId));
    setDraft({ mode: "auto", ids });
  };
  /** 自分で並べる：並びはそのまま、mode だけ */
  const toManual = () => setDraft((d) => (d ? { ...d, mode: "manual" } : d));
  const saveOrder = async () => {
    if (!draft || saving) return;
    setSaving(true);
    setErr(undefined);
    setMenuId(null);
    try {
      // 自分で並べる：使える会社を表示の順に全部（節を上から順につなげる）
      const body: CompanyOrderRequest = draft.mode === "auto" ? { mode: "auto" } : { mode: "manual", tenantIds: sections.flatMap((s) => s.movable.map((c) => c.tenantId)) };
      const m = await api<Me>("PUT", "/account/company-order", body);
      await setMe(qc, m);
      setDraft(null);
    } catch (e) {
      setErr(messageOf(e));
    } finally {
      setSaving(false);
    }
  };

  /** 会社を押した：同じ製品はその場で切り替え（ログインの続きは選ぶ）、ほかの製品は相手のドメインへ移る（★1） */
  const pick = async (c: CompanyChoice) => {
    if (busyId || draft) return;
    setErr(undefined);
    setBusyId(c.tenantId);
    try {
      if (c.selectable) {
        const m = switching
          ? await api<Me>("POST", "/auth/switch", { tenantId: c.tenantId })
          : await api<Me>("POST", "/auth/choose", { chooseToken: st!.chooseToken, tenantId: c.tenantId, remember: st!.remember });
        await setMe(qc, m);
        void qc.invalidateQueries();
        // ログインの続き（自動ログアウトのあと、#189）は覚えた画面へ。切り替えはホーム
        nav(switching ? "/home" : afterLoginPath(), { replace: true, state: switching ? { switched: m.tenant.name } : null });
        return;
      }
      setMoving(true);
      const r = switching
        ? await api<HandoffStartResponse>("POST", "/auth/handoff/start", { tenantId: c.tenantId } satisfies HandoffStartRequest)
        : await api<HandoffStartResponse>("POST", "/auth/choose/handoff", { chooseToken: st!.chooseToken, tenantId: c.tenantId, remember: st!.remember } satisfies ChooseHandoffRequest);
      window.location.assign(r.url); // 移るまで回る印のまま
    } catch (e) {
      if (isChooseExpired(e)) setExpired(true);
      else setErr(messageOf(e));
      setBusyId(null);
      setMoving(false);
    }
  };

  const create = useMutation({
    mutationFn: (name: string) => {
      const person = askName ? { familyName: nf, givenName: ng } : {};
      return switching
        ? api<Me>("POST", "/account/companies", { name, industry, ...person })
        : api<Me>("POST", "/auth/choose/company", { chooseToken: st!.chooseToken, name, remember: st!.remember, industry, ...person });
    },
    onSuccess: async (m) => {
      await setMe(qc, m);
      void qc.invalidateQueries();
      nav("/home", { replace: true, state: { created: m.tenant.name } });
    },
    onError: (e) => {
      // 鍵が使えない（#203）：窓を閉じて、画面の断りと「ログイン画面へ」
      if (isChooseExpired(e)) {
        closeNew();
        setExpired(true);
      } else setCreateErr(messageOf(e));
    },
  });
  const submitNew = () => {
    setCreateErr(undefined);
    if (parsed.success && nameOk && !create.isPending) create.mutate(parsed.data);
  };
  // 窓の Esc は取消（作っている最中は閉じない）
  useEffect(() => {
    if (!creating) return;
    const k = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !create.isPending) closeNew();
    };
    document.addEventListener("keydown", k);
    return () => document.removeEventListener("keydown", k);
  }); // eslint-disable-line react-hooks/exhaustive-deps
  const closeNew = () => {
    setCreating(false);
    setCoName("");
    setCreateErr(undefined);
    setIndustry(DEFAULT_INDUSTRY[P.product]);
    setNf(acct?.familyName ?? "");
    setNg(acct?.givenName ?? "");
  };

  // 切替：ログインしていなければログインへ。ログインの続き：state が無ければ（開き直した）ログインへ
  if (switching) {
    if (me.isPending) return <div className="auth"><p className="hint">読み込んでいます…</p></div>;
    if (!me.data) return <Navigate to="/login" replace />;
  } else if (!st?.chooseToken) {
    return <Navigate to="/login" replace />;
  }
  // 鍵が使えない（#203 の続き）：会社の一覧ではなく止めの画面 j)（どの会社を押しても進めない）
  if (expired) return <LoginExpiredStop />;
  const email = switching ? me.data!.user.email : (st?.email ?? "");
  /** いまの会社の名前（新しい会社を作る窓の注意の帯。ログインの続きでは無い） */
  const curName = switching ? (me.data!.tenant.name ?? "") : "";
  const editing = switching && draft !== null;
  // 「編集」はどれかの節に使える会社が2つ以上あるときだけ（並べ替えは節の中だけ。節ごとに1社なら並べるものが無い）。ログインの続きでは出さない（★g）
  const canEdit = switching && sections.some((s) => s.movable.length >= 2);
  const multi = sections.length > 1;

  const companyRow = (c: CompanyChoice, s: Section) => {
    const id = c.tenantId;
    const busy = busyId === id;
    const movIds = s.movable.map((x) => x.tenantId);
    const k = movIds.indexOf(id);
    const label = c.name || SWITCHER_MSG.noName;
    const sub = !c.usable ? (
      c.blockedReason ? <span className="co-why">{SWITCHER_MSG.blocked[c.blockedReason]}</span> : null
    ) : c.current || c.role === "member" ? (
      <>
        {c.current ? <span className="co-cur">{SWITCHER_MSG.current}</span> : null}
        {c.role === "member" ? <span className="co-role">{SWITCHER_MSG.member}</span> : null}
      </>
    ) : null;
    const end = editing ? null : busy ? (
      <span className="gspin" aria-hidden="true" />
    ) : c.current ? (
      <CheckMark testId="co-check" />
    ) : (
      <>
        {c.last && !switching ? <span className="co-last" data-testid="co-last">{SWITCHER_MSG.last}</span> : null}
        {!c.selectable && c.usable ? <ExtIcon /> : null}
      </>
    );
    const target = drag?.target?.id === id ? drag.target.where : null;
    const cls = [
      c.current && "is-current",
      !c.usable && "is-blocked",
      busy && "is-busy",
      drag?.id === id && "is-dragging",
      target === "above" && "is-dropabove",
      target === "below" && "is-dropbelow",
      menuId === id && "is-open",
    ]
      .filter(Boolean)
      .join(" ");
    return (
      <GroupRow
        key={id}
        rowRef={rowRef(id)}
        className={cls}
        style={drag?.id === id ? { transform: `translateY(${drag.dy}px)` } : undefined}
        current={c.current}
        busy={busy}
        icon={<CompanyIcon name={c.name} logoUrl={c.logoUrl} initial={c.initial} />}
        title={c.name || <span className="co-noname">{SWITCHER_MSG.noName}</span>}
        titleTestId="co-name"
        sub={sub}
        end={end}
        // 押せる行は <button>（使用中は押せない <div>。使えない会社は押せない button）
        onClick={editing || c.current ? undefined : () => void pick(c)}
        disabled={!c.usable || busyId !== null}
        data-testid="co-row"
        data-co={id}
        data-product={c.product}
        after={
          editing && c.usable ? (
            <>
              <GripButton
                label={SWITCHER_MSG.grip(label)}
                expanded={menuId === id}
                testId="co-grip"
                gripRef={(el: HTMLButtonElement | null) => {
                  if (el) gripEls.current.set(id, el);
                  else gripEls.current.delete(id);
                }}
                {...gripProps(id, movIds)}
              />
              {menuId === id ? (
                <MoveMenu
                  label={SWITCHER_MSG.grip(label)}
                  labels={MOVE_LABELS}
                  canUp={k > 0}
                  canDown={k >= 0 && k < movIds.length - 1}
                  onMove={(kind) => moveBy(s, id, kind)}
                  onClose={(f) => {
                    setMenuId(null);
                    if (f) gripEls.current.get(id)?.focus();
                  }}
                  anchor={() => gripEls.current.get(id) ?? null}
                  testId="move-menu"
                />
              ) : null}
            </>
          ) : null
        }
      />
    );
  };

  return (
    <div className="authpage copage" data-product={P.product} data-testid="choose">
      <header className="authtop">
        <a href="/" className="brand">
          <img src={logoHeader} alt={P.name} width={320} height={100} />
        </a>
      </header>
      <NoticeBand items={notices.items} />
      {/* #179：帯は一覧の上に入るので、届くまで（1.5 秒まで）一覧を見せない */}
      <main className="cocol" data-testid="choose-column" hidden={!notices.ready} aria-busy={!notices.ready || undefined}>
        {/* ログインの続き：スマホ版は上の帯を出さないので、頭にロゴ（#115） */}
        {switching ? null : <AuthLogo phoneOnly />}
        {editing ? (
          <div className="cotop is-editing">
            <button type="button" className="btn text cotop-btn" onClick={cancelEdit} data-testid="co-cancel">
              {SWITCHER_MSG.cancel}
            </button>
            <h1 className="cotop-ttl">{SWITCHER_MSG.reorder}</h1>
            <button type="button" className="btn text cotop-btn cotop-done" onClick={() => void saveOrder()} disabled={saving} data-testid="co-done">
              {SWITCHER_MSG.done}
            </button>
          </div>
        ) : switching ? (
          <div className="cotop">
            <BackButton back={back} />
            <span className="cotop-sp" />
            {canEdit ? (
              <button type="button" className="btn text cotop-btn" onClick={startEdit} disabled={busyId !== null} data-testid="co-edit">
                {SWITCHER_MSG.edit}
              </button>
            ) : null}
          </div>
        ) : null}
        {editing ? null : <h1 className="cotitle">{title}</h1>}

        {/* 並び順（編集のときだけ一番上） */}
        {editing ? (
          <GroupSection title={SWITCHER_MSG.orderHead} note={SWITCHER_MSG.autoNote} data-testid="order-section">
            <GroupList plain>
              {(["auto", "manual"] as const).map((m) => (
                <GroupRow
                  key={m}
                  title={m === "auto" ? SWITCHER_MSG.auto : SWITCHER_MSG.manual}
                  pressed={draft!.mode === m}
                  end={draft!.mode === m ? <CheckMark /> : null}
                  onClick={m === "auto" ? toAuto : toManual}
                  buttonTestId={m === "auto" ? "order-auto" : "order-manual"}
                />
              ))}
            </GroupList>
          </GroupSection>
        ) : null}

        {sections.map((s) => (
          <GroupSection
            key={s.product}
            title={multi ? PRODUCTS[s.product].name : undefined}
            icon={multi ? PRODUCTS[s.product].assets.logoMark : undefined}
            titleTestId="co-section-title"
            note={!editing && s.product !== P.product ? SWITCHER_MSG.goesTo(PRODUCTS[s.product].name) : undefined}
            data-testid="co-section"
            data-product={s.product}
          >
            <GroupList>{(editing ? [...s.movable, ...s.blocked] : s.items).map((c) => companyRow(c, s))}</GroupList>
          </GroupSection>
        ))}

        {!switching && companies.every((c) => !c.usable) ? <p className="err">⚠ {USER_MSG.noCompany}</p> : null}
        <ErrorText>{err}</ErrorText>

        {editing ? null : (
          <>
            {canCreate ? (
            <GroupSection>
              <GroupList>
                <GroupRow
                  className="co-addrow"
                  icon={
                    <span className="co-plus" aria-hidden="true">
                      <svg viewBox="0 0 24 24">
                        <path d="M12 5v14M5 12h14" />
                      </svg>
                    </span>
                  }
                  title={SWITCHER_MSG.add}
                  onClick={() => setCreating(true)}
                  disabled={busyId !== null}
                  buttonTestId="co-add"
                />
              </GroupList>
            </GroupSection>
            ) : null}
            <GroupSection>
              <GroupList plain>
                <GroupRow
                  className="co-outrow"
                  title={SWITCHER_MSG.logout}
                  onClick={() => logout.mutate(undefined, { onSettled: () => nav("/login", { replace: true }) })}
                  disabled={logout.isPending}
                  buttonTestId="co-logout"
                />
              </GroupList>
              {email ? (
                <p className="gsecnote" data-testid="co-email">
                  {SWITCHER_MSG.loggedInAs(email)}
                </p>
              ) : null}
            </GroupSection>
            {/* スマホ：PC 専用の機能の案内（#185、docs/74 §1。一覧は contract の pcOnly.ts） */}
            {pcOnly.phone ? (
              <p className="co-pcnote" data-testid="pc-only-note">
                {PC_ONLY_NOTE}
              </p>
            ) : null}
          </>
        )}
      </main>
      <SiteFooter band />
      {back.dialog}
      {/* 移るあいだの読み上げ（回る印は見た目だけ） */}
      <p className="vh" role="status" aria-live="polite">
        {moving ? SWITCHER_MSG.moving : ""}
      </p>

      {creating && canCreate ? (
        <div className="dim" onClick={(e) => e.target === e.currentTarget && !create.isPending && closeNew()}>
          <form
            className="dlg newco"
            role="dialog"
            aria-modal="true"
            aria-labelledby="newco-h"
            aria-describedby="newco-lead newco-warn"
            noValidate
            data-testid="new-company-sheet"
            onSubmit={(e) => {
              e.preventDefault();
              submitNew();
            }}
          >
            <div className="dh" id="newco-h">
              {NEW_COMPANY_MSG.sheetTitle}
            </div>
            <div className="db" data-testid="new-company">
              <p className="newco-lead" id="newco-lead">
                {NEW_COMPANY_MSG.lead}
              </p>
              {/* 注意の帯：いまの会社に利用者を加える操作ではない（#185。招待を受けるつもりで押した人がいた）。
                  #287（2026-11-01 射場さん ★6）：朱の帯はエラーに見える＝藍の淡い地・藍の字・左に ⓘ（止めの画面・エラーの朱と分ける） */}
              <p className="newco-warn" id="newco-warn" data-testid="new-company-warn">
                <InfoIcon size={20} filled />
                <span>{curName ? NEW_COMPANY_MSG.warn(curName) : NEW_COMPANY_MSG.warnNoCurrent}</span>
              </p>
              <div className="glist gfields">
                <Field id="newCoName" label={<>{NEW_COMPANY_MSG.nameLabel}<span className="req">必須</span></>}>
                  <input
                    id="newCoName"
                    className="inp"
                    autoComplete="organization"
                    autoFocus
                    required
                    aria-required="true"
                    aria-invalid={tooLong || undefined}
                    aria-describedby={tooLong ? "newCoNameErr" : undefined}
                    value={coName}
                    onKeyDown={guardComposingEnter}
                    onChange={(e) => {
                      setCoName(e.target.value);
                      setCreateErr(undefined);
                    }}
                  />
                </Field>
                <IndustrySelect id="newCoIndustry" product={P.product} value={industry} onChange={setIndustry} />
              </div>
              {askName ? (
                <GroupSection title={ACCOUNT_NAME_MSG.yourName} className="co-yourname" data-testid="new-company-name">
                  <GroupList>
                    <GroupInputRow id="newCoFamily" label={ACCOUNT_NAME_MSG.family}>
                      <input id="newCoFamily" className="giinp" autoComplete="family-name" maxLength={50} value={nf} onKeyDown={guardComposingEnter} onChange={(e) => setNf(e.target.value)} />
                    </GroupInputRow>
                    <GroupInputRow id="newCoGiven" label={ACCOUNT_NAME_MSG.given}>
                      <input id="newCoGiven" className="giinp" autoComplete="given-name" maxLength={50} value={ng} onKeyDown={guardComposingEnter} onChange={(e) => setNg(e.target.value)} />
                    </GroupInputRow>
                  </GroupList>
                </GroupSection>
              ) : null}
              {tooLong ? (
                <p className="err" id="newCoNameErr" role="alert">
                  ⚠ {NEW_COMPANY_MSG.nameTooLong}
                </p>
              ) : null}
              <p className="newco-note" data-testid="new-company-note">
                {NEW_COMPANY_MSG.note}
              </p>
              {createErr ? (
                <p className="err" role="alert" data-testid="new-company-error">
                  ⚠ {createErr}
                </p>
              ) : null}
            </div>
            <div className="df">
              <span className="sp" />
              <button type="button" className="btn text" onClick={closeNew} disabled={create.isPending} data-testid="new-company-cancel">
                {NEW_COMPANY_MSG.cancel}
              </button>
              <button type="submit" className="btn primary" disabled={!parsed.success || !nameOk || create.isPending} data-testid="new-company-submit">
                {NEW_COMPANY_MSG.submit}
              </button>
            </div>
          </form>
        </div>
      ) : null}
    </div>
  );
}
